import { Component, ElementRef, OnInit, ViewEncapsulation, WritableSignal, effect, inject, input, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NgControl, NgModel, Validators } from '@angular/forms';
import { getRandomId } from 'src/app/shared/extensions/string.extensions';
import { ValidationDisplayType } from './validation-display-type';

@Component({
    selector: 'app-standard-textbox',
    templateUrl: './standard-textbox.component.html',
    styleUrls: ['./standard-textbox.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StandardTextboxComponent implements ControlValueAccessor, OnInit {
    protected readonly control = inject(NgControl, { self: true, optional: true });

    public readonly addOnText = input<string>();
    public readonly label = input<string>('');
    public readonly hint = input<string>('');
    public readonly disabled = input<boolean>(false);
    public readonly validationError = input<string>('');
    public readonly replaceTextbox = input<boolean>(false);
    public readonly maxLength = input<number>(128);
    public readonly focus = input<boolean>(false);
    public readonly validationDisplayType = input<ValidationDisplayType>(ValidationDisplayType.StandardValidation);

    private readonly standardTextbox = viewChild.required<ElementRef<HTMLInputElement>>('standardTextbox');

    public readonly id = getRandomId();
    public data: WritableSignal<string | number | boolean | null> = signal(null);

    public isDisabled = false;
    public hasBoundFormControl = false;
    protected readonly validationDisplayTypes = ValidationDisplayType;

    constructor() {
        if (this.control) {
            this.hasBoundFormControl = !(this.control instanceof NgModel);
            this.control.valueAccessor = this;
        }

        effect(() => {
            if (this.focus()) {
                queueMicrotask(() => this.standardTextbox()?.nativeElement.focus());
            }
        });
    }

    public ngOnInit(): void {
        this.setValidators();
    }

    private setValidators(): void {
        if (!this.control?.control) {
            return;
        }

        const defaultValidators = [Validators.maxLength(this.maxLength())];
        const existing = this.control.control.validator;
        const validators = existing ? [existing, ...defaultValidators] : defaultValidators;

        this.control.control.setValidators(validators);
        this.control.control.updateValueAndValidity();
    }

    public onChangeFn: (value: string | number | boolean | null) => void = () => undefined;
    public onTouchedFn: () => void = () => undefined;

    public writeValue(value: string | number | boolean | null | undefined): void {
        const data = value != null && !(typeof value === 'number' && Number.isNaN(value)) ? value : null;
        this.data.set(data);
    }

    public registerOnChange(fn: (value: string | number | boolean | null) => void): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    public onChange(): void {
        this.onChangeFn(this.data());
    }
}
